import React from 'react'
import { motion } from 'framer-motion'

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-background text-primary">
      {/* Hero */}
      <section className="pt-40 pb-32 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.h1
            className="text-6xl md:text-8xl font-display font-medium tracking-tighter text-white mb-10"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            Industry veterans.<br />
            <span className="text-neutral-500">Business solutions.</span>
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl text-neutral-400 font-light leading-relaxed max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            We're a team of seasoned professionals who've spent years working across design, branding, and digital. We keep things discreet, move fast, and deliver work that performs.
          </motion.p>
        </div>
      </section>

      {/* Philosophy Grid */}
      <section className="py-24 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="p-10 border border-white/5 bg-surfaceHighlight/20 hover:bg-surfaceHighlight/40 transition-colors duration-500"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-6xl font-display font-bold text-white/10 mb-8">01</div>
              <h3 className="text-2xl font-display font-medium text-white mb-4">No Templates</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Every solution is built for your specific needs. We don't do cookie-cutter work. Your brand deserves a unique identity.
              </p>
            </motion.div>

            <motion.div
              className="p-10 border border-white/5 bg-surfaceHighlight/20 hover:bg-surfaceHighlight/40 transition-colors duration-500"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-6xl font-display font-bold text-white/10 mb-8">02</div>
              <h3 className="text-2xl font-display font-medium text-white mb-4">Speed Matters</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Business doesn't wait. We deliver exceptional work in timeframes others can't match, without sacrificing quality.
              </p>
            </motion.div>

            <motion.div
              className="p-10 border border-white/5 bg-surfaceHighlight/20 hover:bg-surfaceHighlight/40 transition-colors duration-500"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-6xl font-display font-bold text-white/10 mb-8">03</div>
              <h3 className="text-2xl font-display font-medium text-white mb-4">Results First</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Pretty designs mean nothing if they don't perform. We build for business impact, conversion, and growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-32 px-6 border-t border-white/5 bg-surfaceHighlight/10">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-medium mb-12 text-white">Who We Are</h2>
            <div className="space-y-8 text-neutral-400 font-light leading-relaxed text-xl">
              <p>
                We're a collective of professionals who've worked in-house at agencies, startups, and established brands. We understand the industry because we're still part of it.
              </p>
              <p>
                <span className="font-bold text-white">chipahauk</span> exists as a separate entity for businesses that need high-caliber creative work without the overhead of traditional agencies. We operate with the efficiency of a tight-knit team and the expertise of industry veterans.
              </p>
              <p>
                We keep a low profile. We stay sharp, stay current, and stay connected to what's actually happening in the industry.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <motion.h2
            className="text-4xl md:text-5xl font-display font-medium mb-16 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            What We Offer
          </motion.h2>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            <motion.div
              className="p-12 bg-background hover:bg-surfaceHighlight/20 transition-colors duration-500"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-display font-medium text-white mb-4">Vehicle Wraps</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Professional fleet graphics designed for maximum visibility and brand impact.
              </p>
            </motion.div>

            <motion.div
              className="p-12 bg-background hover:bg-surfaceHighlight/20 transition-colors duration-500"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-display font-medium text-white mb-4">Brand Identity</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Complete brand systems, visual identity, and design guidelines that scale.
              </p>
            </motion.div>

            <motion.div
              className="p-12 bg-background hover:bg-surfaceHighlight/20 transition-colors duration-500"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-display font-medium text-white mb-4">Web Design</h3>
              <p className="text-neutral-400 font-light leading-relaxed">
                Custom digital experiences built for performance and conversions.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h2
            className="text-5xl md:text-7xl font-display font-medium text-white mb-8 tracking-tighter"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Let's work.
          </motion.h2>
          <motion.p
            className="text-xl text-neutral-400 mb-12 font-light"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
          >
            If you need business-focused creative work delivered fast, get in touch.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <a
              href="mailto:hello@chipahauk.com"
              className="inline-flex items-center px-10 py-5 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-accent hover:text-white transition-all duration-300"
            >
              Get in touch
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About
